import {
  mdiClose,
  mdiDownload,
  mdiFileDelimited,
  mdiFileExcel,
  mdiFilter,
  mdiPin,
  mdiRefresh,
  mdiSortAscending,
  mdiSortDescending,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Button, TextField } from '@mollybet/ui';
import { api } from 'api';
import { Currency, isCurrencyString } from 'common/classes/currency';
import { CurrencyCodes } from 'common/configs/currency';
import { transparentize } from 'polished';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import {
  BaseTable,
  ColumnProp,
  DownloadCsv,
  TotalCell,
  TotalRow,
} from '../base';
import { FiltersContainer } from '../filtering';
import { BaconColumnType } from './bacon';
import { isJerkyDynamicDateFilter, JerkyEngine, JerkyRow } from './jerky';

export interface BaconTableProps {
  url: string;
  defaultQ?: string;
  onChange?: (q: string) => void;
  currencyColumns?: Set<string>;
  totalColumns?: Set<string>;
}

type CurrencyTotal = { [code in CurrencyCodes]: number };

export const NavContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 1rem;
  grid-row-gap: 0.5rem;
  margin: 1rem 0;
  align-items: center;
  max-width: calc(100vw - 2rem);
`;

export const NavValue = styled.div<{ state?: 'active' | 'disabled' }>`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0.125rem;
  padding: 0.25rem 0.375rem;
  box-sizing: border-box;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  height: 1.25rem;
  text-transform: uppercase;
  font-weight: bold;
  background-color: ${({ theme, state }) =>
    state === 'active' ? theme.cardLevel1 : undefined};
  opacity: ${({ state }) => (state === 'disabled' ? 0.5 : undefined)};
  border-radius: 2px;
  transition: opacity 300ms, background-color 300ms;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.cardLevel2};
  }

  span {
    margin-left: 0.125rem;
  }
`;

export const ColumnHeader = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto;
  grid-column-gap: 0.25rem;
`;

export const ColumnTitle = styled.div`
  margin-bottom: 0.25rem;
`;

export const ColumnIcons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const ColumnIcon = styled.div`
  margin-bottom: 0.25rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 300ms;
  border-radius: 2px;
  height: 1.5rem;
  width: 1.5rem;

  &:hover {
    background-color: ${({ theme }) => transparentize(0.5, theme.background)};
  }
`;

export const MetaTitle = styled(ColumnTitle)`
  margin-bottom: 0.125rem;
  font-size: 0.75rem;
  margin-bottom: 0;
`;

export const MetaIcons = styled(ColumnIcons)``;

export const MetaIcon = styled(ColumnIcon)`
  margin-bottom: 0.125rem;
  height: 1rem;
  width: 1rem;
`;

export const RowValue = styled.div`
  width: ${({ onClick }) => (onClick ? 'fit-content' : undefined)};
  padding-bottom: 0.125rem;
  border-bottom: ${({ onClick }) => (onClick ? '1px dashed' : undefined)};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : undefined)};
`;

export const PivotTitle = styled.div<{ state?: 'active' | 'disabled' }>`
  display: flex;
  align-items: center;
  background-color: ${({ theme, state }) =>
    state === 'active' ? theme.cardLevel1 : undefined};
  opacity: ${({ state }) => (state === 'disabled' ? 0.5 : undefined)};
`;

export const PivotTitleIcon = styled.div`
  margin-right: 0.5rem;
  cursor: pointer;
`;

export const PivotValue = styled(NavValue)`
  display: inline-flex;
  align-items: center;
`;

export const PivotIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 300ms;
  padding: 0.25rem;

  &:hover {
    background-color: ${({ theme }) => theme.cardLevel1};
  }
  &:last-child {
    margin-right: 0.25rem;
  }
`;

export const WidgetButton = styled(Button)`
  margin-right: 0.5rem;
  width: 4.5rem;
`;

export const SubRowGenerator =
  (
    columns: (ColumnProp<JerkyRow> & { total?: string })[],
    rows: JerkyRow[],
    currencyColumns?: Set<string>,
    totalColumns?: Set<string>
  ) =>
  ({ index, style }): JSX.Element => {
    if (index !== rows.length - 1) return null;
    if (!totalColumns?.size) return null;

    const subrows: JSX.Element[] = [];
    const totals: { [key: string]: Partial<CurrencyTotal> | number } = {};
    const currencies: Set<string> = new Set();

    rows.forEach((row) =>
      row.forEach(({ value }, _index) => {
        const column = columns[_index];
        const label = column.key.split('_')[0];
        if (!totalColumns?.has(label)) return;

        if (currencyColumns?.has(label) || isCurrencyString(value)) {
          const currency = new Currency(value as unknown as string);
          if (currency.value) {
            totals[column.key] = (totals[column.key] ||
              {}) as Partial<CurrencyTotal>;
            totals[column.key][currency.code] =
              totals[column.key][currency.code] ||
              new Currency(0, currency.code);
            totals[column.key][currency.code].value += currency.value;
            currencies.add(currency.code);
          }
        } else {
          totals[column.key] = totals[column.key] || 0;
          if (value && value.toString().match(/[0-9.]/g)) {
            totals[column.key] =
              (totals[column.key] as number) +
              parseFloat(
                (value || '0')
                  .toString()
                  .match(/[0-9.]/g)
                  .join('')
              );
          }
        }
      })
    );

    const orderedCurrencies = Array.from(currencies).sort();
    let needsRowName = true;
    subrows.push(
      <TotalRow style={style} key="page-totals" role="row">
        {columns.map((column, index) => {
          if (column.key in totals)
            return (
              <TotalCell
                key={column.key}
                role="cell"
                style={{ gridColumn: index + 1, background: 'transparent' }}
              >
                {typeof totals[column.key] === 'number'
                  ? totals[column.key]
                  : orderedCurrencies.map((currency) => (
                      <React.Fragment key={currency}>
                        {totals[column.key][currency]?.toElement() ||
                          new Currency(undefined, currency).toElement()}
                      </React.Fragment>
                    ))}
              </TotalCell>
            );

          if (needsRowName) {
            needsRowName = false;
            return (
              <TotalCell
                key={column.key}
                role="cell"
                style={{ gridColumn: index + 1, background: 'transparent' }}
              >
                Page Total
              </TotalCell>
            );
          }
          return null;
        })}
      </TotalRow>
    );

    if (columns.some((column) => column.total != null && column.total !== '')) {
      needsRowName = true;
      subrows.push(
        <TotalRow style={style} key="query-totals" role="row">
          {columns.map((column, index) => {
            if (column.total != null && column.total !== '')
              return (
                <TotalCell
                  key={column.key}
                  role="cell"
                  style={{ gridColumn: index + 1, background: 'transparent' }}
                >
                  {isCurrencyString(column.total)
                    ? new Currency(column.total).toElement()
                    : column.total}
                </TotalCell>
              );

            if (needsRowName) {
              needsRowName = false;
              return (
                <TotalCell
                  key={column.key}
                  role="cell"
                  style={{ gridColumn: index + 1, background: 'transparent' }}
                >
                  Query Total
                </TotalCell>
              );
            }
            return null;
          })}
        </TotalRow>
      );
    }

    return <>{subrows}</>;
  };

export const BaconTable = ({
  url,
  defaultQ,
  currencyColumns = new Set(),
  totalColumns = new Set(),
}: BaconTableProps) => {
  const location = useLocation();
  const [, setHash] = useState(0);
  const jerky = useRef(new JerkyEngine(url, (hash) => setHash(hash), {}));
  const [rawBacon, setRawBacon] = useState(jerky.current.serialiseLocalData());

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    jerky.current.setRawQuery(
      params.get('q') || defaultQ,
      params.get('t') || jerky.current.serialisePagination()
    );
  }, [location.search, defaultQ]);

  useEffect(() => {
    setRawBacon(jerky.current.serialiseLocalData());
  }, [jerky.current.hash, jerky.current.localData]);

  const columns = useMemo(
    (): (ColumnProp<JerkyRow> & { total?: string })[] =>
      jerky.current.localData.columns.map((column, index) => ({
        key: `${column.label}_${index}`,
        title: (
          <ColumnHeader>
            <ColumnTitle>{column.label}</ColumnTitle>
            <ColumnIcons>
              {column.drop ? (
                <ColumnIcon
                  onClick={() => jerky.current.setRawQuery(column.drop)}
                >
                  <Icon path={mdiClose} size={0.75} />
                </ColumnIcon>
              ) : null}
              {column.pivot ? (
                <ColumnIcon
                  onClick={() => jerky.current.setRawQuery(column.pivot)}
                >
                  <Icon path={mdiPin} size={0.75} />
                </ColumnIcon>
              ) : null}
              {column.resetOrder ? (
                <ColumnIcon
                  onClick={() => jerky.current.setRawQuery(column.resetOrder)}
                >
                  <Icon path={mdiRefresh} size={0.75} />
                </ColumnIcon>
              ) : null}
              {column.orderDesc ? (
                <ColumnIcon
                  onClick={() => jerky.current.setRawQuery(column.orderDesc)}
                >
                  <Icon path={mdiSortDescending} size={0.75} />
                </ColumnIcon>
              ) : null}
              {column.orderAsc ? (
                <ColumnIcon
                  onClick={() => jerky.current.setRawQuery(column.orderAsc)}
                >
                  <Icon path={mdiSortAscending} size={0.75} />
                </ColumnIcon>
              ) : null}
            </ColumnIcons>
            {column.type === BaconColumnType.TOTAL ? (
              <>
                <MetaTitle>Total</MetaTitle>
                <MetaIcons />
              </>
            ) : null}
            {column.type === BaconColumnType.VALUE ? (
              <>
                {jerky.current.localData.pivotOrder.map((pivot) =>
                  column?.pivots?.[pivot] ? (
                    <React.Fragment key={pivot}>
                      <MetaTitle>{column.pivots[pivot]}</MetaTitle>
                      <MetaIcons>
                        <MetaIcon
                          onClick={() => {
                            jerky.current.togglePivotFilterValue(
                              pivot,
                              column.pivots[pivot]
                            );
                            jerky.current.fetch();
                          }}
                        >
                          <Icon path={mdiFilter} size={0.5} />
                        </MetaIcon>
                        <MetaIcon
                          onClick={() => {
                            jerky.current.togglePivotHideValue(
                              pivot,
                              column.pivots[pivot]
                            );
                            jerky.current.fetch();
                          }}
                        >
                          <Icon path={mdiClose} size={0.5} />
                        </MetaIcon>
                      </MetaIcons>
                    </React.Fragment>
                  ) : null
                )}
              </>
            ) : null}
          </ColumnHeader>
        ),
        valueFormatter: (row) => (
          <RowValue
            onClick={
              row[index]?.filter
                ? () => jerky.current.setRawQuery(row[index].filter)
                : undefined
            }
          >
            {column.type === BaconColumnType.ACTION ? (
              <Button
                onClick={() => {
                  if (column.label === 'Clear') {
                    api.post(
                      `${jerky.current.bacon.endpoint}/clear`,
                      row[index].value,
                      {
                        baseURL: jerky.current.bacon.baseUrl,
                      }
                    );
                    window.location.reload();
                  } else {
                    jerky.current.setRawQuery(row[index].action);
                  }
                }}
              >
                {column.label}
              </Button>
            ) : currencyColumns?.has(column.label) ||
              isCurrencyString(row[index].value) ? (
              new Currency(row[index].value).toElement()
            ) : (
              row[index].value
            )}
          </RowValue>
        ),
        total: column.total,
        csvFormatter: (row) =>
          column.type === BaconColumnType.ACTION
            ? column.label
            : currencyColumns?.has(column.label) ||
              isCurrencyString(row[index].value)
            ? new Currency(row[index].value).toString({ symbol: false })
            : row[index].value,
      })),
    // eslint-disable-next-line
    [jerky.current.hash, jerky.current.localData]
  );

  const csvExtra = useMemo(() => {
    if (!jerky.current.localData.rows.length) return '';
    if (!totalColumns?.size) return '';

    const totals: { [key: string]: Partial<CurrencyTotal> | number } = {};
    const currencies: Set<string> = new Set();
    let extra = '';

    jerky.current.localData.rows.forEach((row) =>
      row.forEach(({ value }, _index) => {
        const column = columns[_index];
        const label = column.key.split('_')[0];
        if (!totalColumns?.has(label)) return;

        if (value === '-') {
          // Skip empty values
        } else if (currencyColumns?.has(label) || isCurrencyString(value)) {
          const currency = new Currency(value as unknown as string);
          if (currency.value) {
            totals[column.key] = (totals[column.key] ||
              {}) as Partial<CurrencyTotal>;
            totals[column.key][currency.code] =
              totals[column.key][currency.code] ||
              new Currency(0, currency.code);
            totals[column.key][currency.code].value += currency.value;
            currencies.add(currency.code);
          }
        } else {
          totals[column.key] = totals[column.key] || 0;
          if (value && value.toString().match(/[0-9.]/g)) {
            totals[column.key] =
              (totals[column.key] as number) +
              parseFloat(
                (value || '0')
                  .toString()
                  .match(/[0-9.]/g)
                  .join('')
              );
          }
        }
      })
    );

    const orderedCurrencies = Array.from(currencies).sort();
    let needsRowName = true;
    extra += columns
      .map((column) => {
        if (column.key in totals) {
          const symbol =
            Object.keys(totals[column.key]).length === 1 ? false : 'code';
          return `"${
            typeof totals[column.key] === 'number'
              ? totals[column.key]
              : orderedCurrencies
                  .map(
                    (currency) =>
                      totals[column.key][currency]?.toString({ symbol }) ||
                      new Currency(undefined, currency).toString({ symbol })
                  )
                  .join(' \n')
          }"`;
        }

        if (needsRowName) {
          needsRowName = false;
          return 'Page Total';
        }

        return '';
      })
      .join(',');

    if (columns.some((column) => column.total != null && column.total !== '')) {
      needsRowName = true;
      extra += '\n';
      extra += columns
        .map((column) => {
          if (column.total != null && column.total !== '')
            return `"${
              isCurrencyString(column.total)
                ? new Currency(column.total).toString({ symbol: false })
                : column.total
            }"`;

          if (needsRowName) {
            needsRowName = false;
            return 'Query Total';
          }

          return '';
        })
        .join(',');
    }

    return extra;
    // eslint-disable-next-line
  }, [
    jerky.current.hash,
    jerky.current.localData,
    columns,
    currencyColumns,
    totalColumns,
  ]);

  const dynamicFilters = useMemo(() => {
    const dynamicFilterColumns: string[] = [];
    Object.values(jerky.current.localData.dynamicFilters).forEach((filter) => {
      if (isJerkyDynamicDateFilter(filter)) {
        dynamicFilterColumns.push(filter.fromPart.column);
        dynamicFilterColumns.push(filter.toPart.column);
      }
    });

    return dynamicFilterColumns;
    // eslint-disable-next-line
  }, [jerky.current.hash, jerky.current.localData]);

  const onRawBaconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRawBacon(event.currentTarget.value?.replace(/[^\w:\-/]/, ''));
  };

  console.log(jerky.current);

  return (
    <>
      <FiltersContainer title="Table Controls">
        <NavContainer>
          {Object.values(jerky.current.localData.dimensions).map(
            (dimension) => (
              <React.Fragment key={dimension.label}>
                <span>{dimension.label}</span>
                <div>
                  {Object.values(dimension.axes).map((axis) => (
                    <NavValue
                      key={axis.label}
                      state={axis.isSelected ? 'active' : undefined}
                      onClick={() =>
                        jerky.current.toggleAxis(dimension.label, axis.label)
                      }
                    >
                      {axis.label}
                    </NavValue>
                  ))}
                </div>
              </React.Fragment>
            )
          )}
          {Object.values(jerky.current.localData.dynamicFilters).map(
            (dynamicFilter) => {
              if (isJerkyDynamicDateFilter(dynamicFilter))
                return (
                  <React.Fragment key={dynamicFilter.label}>
                    <span>{dynamicFilter.label}</span>
                    <div>
                      <TextField
                        type="date"
                        value={dynamicFilter.value.from || ''}
                        onChange={(event) =>
                          jerky.current.setDateFilter(dynamicFilter.label, {
                            ...dynamicFilter.value,
                            from: event.currentTarget.value,
                          })
                        }
                        label="From"
                        shrinked
                      />
                      <TextField
                        type="date"
                        value={dynamicFilter.value.to || ''}
                        onChange={(event) =>
                          jerky.current.setDateFilter(dynamicFilter.label, {
                            ...dynamicFilter.value,
                            to: event.currentTarget.value,
                          })
                        }
                        label="To"
                        shrinked
                      />
                    </div>
                  </React.Fragment>
                );
              return null;
            }
          )}
          {Object.values(jerky.current.localData.values).length ? (
            <>
              <span>Hidden Values</span>
              <div>
                {Object.values(jerky.current.localData.values).map((value) => (
                  <NavValue
                    key={value.baconPart.column}
                    state={value.isSelected ? 'disabled' : undefined}
                    onClick={() => jerky.current.toggleValue(value.label)}
                  >
                    <Icon path={mdiClose} size={0.5} />
                    <span>{value.label}</span>
                  </NavValue>
                ))}
              </div>
            </>
          ) : null}
          {Object.values(jerky.current.localData.hideValues).length ? (
            <>
              <span>Hide Values</span>
              <div>
                {Object.values(jerky.current.localData.hideValues).map(
                  (hideValue) => (
                    <NavValue
                      state={hideValue.isSelected ? undefined : 'disabled'}
                      onClick={() =>
                        jerky.current.toggleHideValue(hideValue.label)
                      }
                      key={hideValue.baconPart.column}
                    >
                      <Icon path={mdiClose} size={0.5} />
                      <span>{hideValue.label}</span>
                    </NavValue>
                  )
                )}
              </div>
            </>
          ) : null}
          <span>Filters</span>
          <div>
            {Object.values(jerky.current.localData.activeFilters).map(
              (activeFilter) => {
                if (!dynamicFilters.includes(activeFilter.baconPart?.column))
                  return (
                    <NavValue
                      key={activeFilter.label}
                      state={activeFilter.isSelected ? undefined : 'disabled'}
                      onClick={() =>
                        jerky.current.toggleActiveFilter(activeFilter.label)
                      }
                    >
                      <Icon path={mdiClose} size={0.5} />
                      <span>{activeFilter.label}</span>
                    </NavValue>
                  );
                return null;
              }
            )}
          </div>
        </NavContainer>
      </FiltersContainer>
      {Object.values(jerky.current.localData.pivots).length ? (
        <FiltersContainer title="Pivots">
          <NavContainer>
            {Object.values(jerky.current.localData.pivots).map((pivot) => (
              <React.Fragment key={pivot.label}>
                <PivotTitle
                  state={pivot.isSelected ? undefined : 'disabled'}
                  onClick={() => jerky.current.togglePivot(pivot.label)}
                >
                  <PivotTitleIcon>
                    <Icon path={mdiClose} size={0.75} />
                  </PivotTitleIcon>
                  {pivot.label}
                </PivotTitle>
                <div>
                  {Object.values(pivot.values).map((value) => (
                    <React.Fragment key={value.label}>
                      <PivotValue
                        state={
                          value.isFiltered
                            ? 'active'
                            : value.isHidden
                            ? 'disabled'
                            : undefined
                        }
                      >
                        <PivotIconContainer
                          onClick={() =>
                            jerky.current.togglePivotHideValue(
                              pivot.label,
                              value.label
                            )
                          }
                        >
                          <Icon path={mdiClose} size={0.5} />
                        </PivotIconContainer>
                        <PivotIconContainer
                          onClick={() =>
                            jerky.current.togglePivotFilterValue(
                              pivot.label,
                              value.label
                            )
                          }
                        >
                          <Icon path={mdiFilter} size={0.5} />
                        </PivotIconContainer>
                        {value.label}
                      </PivotValue>
                    </React.Fragment>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </NavContainer>
        </FiltersContainer>
      ) : null}
      <FiltersContainer title="Advanced">
        <NavContainer>
          <div>Raw Bacon</div>
          <div>
            <TextField
              style={{ width: 640 }}
              value={rawBacon}
              onChange={onRawBaconChange}
            />
            <Button
              variant="primary"
              onClick={() => {
                jerky.current.setRawQuery(rawBacon);
              }}
              disabled={rawBacon === jerky.current.bacon.query}
            >
              Submit
            </Button>
          </div>
        </NavContainer>
      </FiltersContainer>
      <br />
      <div>
        <Button onClick={() => jerky.current.setRawQuery('')}>
          Clear All Controls
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            jerky.current.setRawQuery(rawBacon);
          }}
          disabled={!jerky.current.navHasChanged}
        >
          Submit
        </Button>
      </div>
      <br />
      <BaseTable
        columns={columns}
        rows={jerky.current.localData.rows || []}
        SubRow={SubRowGenerator(
          columns,
          jerky.current.localData.rows || [],
          currencyColumns,
          totalColumns
        )}
        csvExtra={csvExtra}
        extraButtons={Object.values(jerky.current.localData.downloads).map(
          (download) => (
            <DownloadCsv href={download.url} key={download.url}>
              <Icon
                path={
                  download.name === 'csv'
                    ? mdiFileDelimited
                    : download.name === 'excel'
                    ? mdiFileExcel
                    : mdiDownload
                }
                size={0.675}
              />
              <div>Bacon {download.label}</div>
            </DownloadCsv>
          )
        )}
        isLoading={jerky.current.isLoading}
        error={jerky.current.error}
        isSortable={false}
        pagination={{
          pageSize: jerky.current.pagination.limit,
          pageNo: jerky.current.pagination.offset + 1,
          count: jerky.current.pagination.count,
          setPageNo: (no) => {
            jerky.current.pagination.offset = no - 1;
            jerky.current.fetch();
          },
          setPageSize: (size) => {
            jerky.current.pagination.offset = 0;
            jerky.current.pagination.limit = size;
            jerky.current.fetch();
          },
        }}
      />
    </>
  );
};
